import { Component, OnInit } from "@angular/core";
import { JsignalService } from "src/app/shared/services/jsignal.service";
import { LocalStorageSettingsService } from "src/app/shared/services/local-storage-settings.service";
import { GenericBadgeRendererComponent } from "src/app/shared/common/generic-badge-renderer.component";
import { AgGridActionbutton } from "src/app/shared/common/AgGridActionbutton";
import { JSIGNALBUTTON } from "src/app/shared/common/JsignalButtons";

export interface ActivityItem {
  id: string;
  productName: string;
  moduleName: string;
  subModule: string;
  transactionType: "New" | "Modified" | "Deleted";
  oldValue: string;
  newValue: string;
  date: string;
  userName: string;
}

@Component({
    selector: "app-key-changes-log",
    templateUrl: "./key-changes-log.component.html",
    styleUrls: ["./key-changes-log.component.scss"],
    standalone: false
})
export class KeyChangesLogComponent implements OnInit {

  /* =====================================================
     CORE VARIABLES
  ===================================================== */

  activities: ActivityItem[] = [];

  fromDate!: Date;
  toDate!: Date;

  backgroundColorScreen = "";

  /* =====================================================
     AG GRID VARIABLES
  ===================================================== */

  keyChangesData: ActivityItem[] = [];
  originalData: ActivityItem[] = []; // preserve original dataset

  columnDefs: any;
  defaultColDef: any;
  gridOptions: any;
  rowClassRules: any;
  getRowStyle: any;
  components: any;

  gridApi: any;
  gridColumnApi: any;

  /* =====================================================
     FILTER VARIABLES
  ===================================================== */

  selectedProduct: string | null = null;
  selectedModule: string | null = null;
  selectedUser: string | null = null;

  productOptions: any[] = [];
  moduleOptions: any[] = [];
  userOptions: any[] = [];

  /* =====================================================
     CONSTRUCTOR
  ===================================================== */

  constructor(
    private jService: JsignalService,
    private localStorage: LocalStorageSettingsService
  ) {
    this.components = {
      jsignalButton: JSIGNALBUTTON,
      buttonRenderer: AgGridActionbutton,
      badgeRenderer: GenericBadgeRendererComponent
    };
  }

  /* =====================================================
     INIT
  ===================================================== */

  ngOnInit(): void {

    this.setDefaultDateRange();

    this.backgroundColorScreen =
      this.localStorage.getSettings("ColorD");

    this.initializeGrid();

    this.fetchKeyChanges();

  }

  /* =====================================================
     GRID INITIALIZATION
  ===================================================== */

  initializeGrid(): void {

    this.columnDefs = [

      {
        headerName: "Product Name",
        field: "productName",
        width: 200,
        filter: true,
        ...this.jService.autoTooltip("Product Name")
      },

      {
        headerName: "Module Name",
        field: "moduleName",
        width: 180,
        filter: true,
        ...this.jService.autoTooltip("Module Name")
      },

      {
        headerName: "Sub Module",
        field: "subModule",
        width: 180,
        filter: true,
        ...this.jService.autoTooltip("Sub Module")
      },

      {
        headerName: "Transaction Type",
        field: "transactionType",
        width: 160,
        filter: true,
        ...this.jService.autoTooltip("Transaction Type")
      },

      {
        headerName: "Old Value",
        field: "oldValue",
        width: 220,
        filter: true,
        ...this.jService.autoTooltip("Old Value")
      },

      {
        headerName: "New Value",
        field: "newValue",
        width: 220,
        filter: true,
        ...this.jService.autoTooltip("New Value")
      },

      {
        headerName: "Modified Date",
        field: "date",
        width: 180,
        filter: "agDateColumnFilter",
        ...this.jService.autoTooltip("Modified Date")
      },

      {
        headerName: "User Name",
        field: "userName",
        width: 180,
        filter: true,
        ...this.jService.autoTooltip("User Name")
      }

    ];

    this.defaultColDef = {
filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      },
      sortable: true,
      filter: true,
      resizable: true
    };

    this.gridOptions = {
      rowHeight: 40,
      pagination: true,
      paginationPageSize: 20
    };

    this.rowClassRules = {

      deletedRow: (params: any) =>
        params.data?.transactionType === "Deleted"

    };

   

  }

  /* =====================================================
     GRID READY
  ===================================================== */

  onGridReady(params: any): void {

    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

  }

  /* =====================================================
     FETCH DATA (MOCK)
  ===================================================== */

  fetchKeyChanges(): void {

    const mockData: ActivityItem[] = [

      {
        id: crypto.randomUUID(),
        productName: "Crocin",
        moduleName: "Product Strategy",
        subModule: "State Change",
        transactionType: "New",
        oldValue: "-",
        newValue: "Draft created",
        date: "2026-02-01 10:15:00",
        userName: "Amit Sharma"
      },

      {
        id: crypto.randomUUID(),
        productName: "Crocin",
        moduleName: "Product Strategy",
        subModule: "Review Workflow",
        transactionType: "Modified",
        oldValue: "Draft",
        newValue: "In Review",
        date: "2026-02-02 14:30:00",
        userName: "Priya Nair"
      },

      {
        id: crypto.randomUUID(),
        productName: "Dolo 650",
        moduleName: "Product Strategy",
        subModule: "Version Management",
        transactionType: "New",
        oldValue: "-",
        newValue: "Version v1 created",
        date: "2026-02-04 09:00:00",
        userName: "Sneha Reddy"
      },

      {
        id: crypto.randomUUID(),
        productName: "Ibuprofen",
        moduleName: "Signal Management",
        subModule: "Signal Update",
        transactionType: "Modified",
        oldValue: "Low",
        newValue: "High",
        date: "2026-02-08 12:00:00",
        userName: "System"
      },

      {
        id: crypto.randomUUID(),
        productName: "Aspirin",
        moduleName: "Risk Management",
        subModule: "State Change",
        transactionType: "Deleted",
        oldValue: "Active",
        newValue: "Removed",
        date: "2026-02-09 15:25:00",
        userName: "Admin User"
      }

    ];

    this.originalData = mockData;
    this.keyChangesData = [...mockData];

    this.populateDropdownOptions();

  }

  /* =====================================================
     DROPDOWN OPTIONS FROM DATA
  ===================================================== */

  populateDropdownOptions(): void {

    this.productOptions =
      this.createDropdownOptions("productName");

    this.moduleOptions =
      this.createDropdownOptions("moduleName");

    this.userOptions =
      this.createDropdownOptions("userName");

  }

  createDropdownOptions(field: string): any[] {

    const uniqueValues =
      [...new Set(this.originalData.map(x => x[field]))];

    return uniqueValues.map(value => ({
      label: value,
      value: value
    }));

  }

  /* =====================================================
     FILTER LOGIC
  ===================================================== */

  applyFilters(): void {

    let filteredData = [...this.originalData];

    if (this.selectedProduct)
      filteredData =
        filteredData.filter(d =>
          d.productName === this.selectedProduct);

    if (this.selectedModule)
      filteredData =
        filteredData.filter(d =>
          d.moduleName === this.selectedModule);

    if (this.selectedUser)
      filteredData =
        filteredData.filter(d =>
          d.userName === this.selectedUser);

    this.keyChangesData = filteredData;

    if (this.gridApi)
      this.gridApi.setGridOption('rowData', filteredData);

  }

  /* =====================================================
     RUN BUTTON
  ===================================================== */

  onRun(): void {

    this.applyFilters();

  }

  /* =====================================================
     DATE HELPERS
  ===================================================== */

  formatDate(date: Date): string {

    return date.toISOString().split("T")[0];

  }

  setDefaultDateRange(): void {

    const today = new Date();

    this.toDate = today;

    this.fromDate = new Date();
    this.fromDate.setMonth(today.getMonth() - 1);

  }

  /* =====================================================
     EXPORT
  ===================================================== */

  onDownload(): void {

    if (this.gridApi)
      this.gridApi.exportDataAsCsv({
        fileName: "KeyChanges.csv"
      });

  }

  /* =====================================================
     REFRESH
  ===================================================== */

  onRefresh(): void {

    this.fetchKeyChanges();

  }

}
